import { usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import type { SharedProps } from '@/types'
import LocaleSwitcher from '@/components/LocaleSwitcher'
import { t } from '@/lib/i18n'

/**
 * 顶栏上常驻的语言入口：一个 🌐 按钮显示当前语言，点开是三选。
 *
 * 为什么不塞进头像下拉：那样得先点头像才看得见，用户根本不知道能切语言。
 * 语言是「一眼要能找到」的东西，尤其对英文访客——他落到一屏中文界面时，
 * 需要的是当场看得见的出口，而不是去翻菜单。
 *
 * 按钮上显示的是**当前生效的语言**（locale），不是三选值（locale_setting）：
 * 用户选了「自动」时，他想知道的是「现在到底是中文还是英文」。
 * 选中态由展开后的 ✓ 表示，那里用 locale_setting，所以「自动」也能正确高亮。
 */
export default function HeaderLocaleMenu() {
  const { locale } = usePage<SharedProps>().props
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [open])

  const currentLabel = locale === 'zh-CN' ? t('account.language_zh') : t('account.language_en')

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={t('account.language')}
        aria-label={t('account.language')}
        // 稳定的测试锚点：aria-label 会随语言变，系统测试不该为了找它先猜当前是哪种语言
        data-locale-menu
        className="flex items-center gap-1 pl-2 pr-2.5 py-1.5 rounded-full border border-line hover:border-brand text-sm font-semibold text-muted whitespace-nowrap"
      >
        🌐<span className="hidden md:inline">{currentLabel}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-card border border-line rounded-xl card-shadow py-1 text-sm z-30">
          <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-faint uppercase tracking-wide">
            {t('account.language')}
          </div>
          <LocaleSwitcher variant="menu" />
        </div>
      )}
    </div>
  )
}
